import React from 'react'
import styled from '@emotion/styled'
import { createPortal } from 'react-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Icon } from '@mobi/component-library/Common/V2'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Theme/Common'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { Input } from '@mobi/component-library/Common/V2/Inputs/Input'
// TEMPORARY: AML Compliance - Bio/description edit field hidden
// import { TextArea } from '@mobi/component-library/Common/V2/Inputs/TextArea'
import { queryClient } from '@core/Data/ReactQuery'
import { putBetLoopProfileDetails } from '@core/Areas/BetLoop/helpers/api'
import { betLoopPrincipleProfileQuery } from '@core/Areas/BetLoop/helpers/queries'
import type { BetLoopProfile } from '@core/Areas/BetLoop/types'
import {
  trackBetLoopAvatarUpdated,
  trackBetLoopEditAvatar,
  trackBetLoopEditMyProfile,
  trackBetLoopProfileUpdated,
} from '@core/Areas/BetLoop/helpers/analytics'
import { isBetLoopProblemResponse } from '@core/Areas/BetLoop/helpers/api/typeGuards'
import { EditAvatar } from './Components/EditAvatar'
import { ProfileAvatar } from '../Common/ProfileAvatar'

type Props = {
  shouldAutoOpenTray?: boolean
  onCompleteOnboarding?(): void
}

export const EditMyProfile: React.FC<Props> = ({ onCompleteOnboarding, shouldAutoOpenTray }) => {
  const { isLoading, data } = useQuery(betLoopPrincipleProfileQuery)

  if (isLoading || !data) return null

  return (
    <EditMyProfileComponent
      myProfile={data}
      onCompleteOnboarding={onCompleteOnboarding}
      shouldAutoOpenTray={shouldAutoOpenTray}
    />
  )
}

// ===============
// Local Component
// ===============

const EditMyProfileComponent: React.FC<Props & { myProfile: BetLoopProfile }> = ({
  myProfile,
  onCompleteOnboarding,
  shouldAutoOpenTray,
}) => {
  const [username, setUsername] = React.useState<string>(myProfile.displayName)
  const [bio, setBio] = React.useState<string>(myProfile.description || '')

  const [usernameError, setUsernameError] = React.useState('')
  const [bioError, setBioError] = React.useState('')

  const [emoji, setEmoji] = React.useState<string | null>(myProfile.avatar || null)
  const [color, setColor] = React.useState<string | null>(myProfile.color || null)

  const [isTrayOpen, setIsTrayOpen] = React.useState(false)
  const [isEditingProfile, setIsEditingProfile] = React.useState(true)

  const { mutate: saveProfile, isPending: isPendingProfile } = useMutation({
    mutationFn: () =>
      putBetLoopProfileDetails({
        displayName: username,
        description: bio,
      }),
    onSuccess: () => {
      setIsTrayOpen(false)
      onCompleteOnboarding && setTimeout(onCompleteOnboarding, 400)
      addToast({
        id: 'profile-edit-success',
        title: 'Profile updated successfully.',
        icon: 'success',
        theme: 'dark',
      })
      trackBetLoopProfileUpdated()
      queryClient.invalidateQueries({
        exact: true,
        refetchType: 'active',
        queryKey: betLoopPrincipleProfileQuery.queryKey,
      })
    },
    onError: async err => {
      if (isBetLoopProblemResponse(err)) {
        err.detail?.includes('bio') ? setBioError(err.detail) : setUsernameError(err.detail)
      }
      addToast({
        id: 'profile-edit-error',
        title: 'Failed to edit profile',
        icon: 'error',
        theme: 'dark',
      })
    },
  })

  const { mutate: saveAvatar, isPending: isPendingAvatar } = useMutation({
    mutationFn: () => putBetLoopProfileDetails({ avatar: emoji, color: color }),
    onSuccess: () => {
      setIsEditingProfile(true)
      queryClient.invalidateQueries(betLoopPrincipleProfileQuery)
      trackBetLoopAvatarUpdated()
    },
  })

  const hasError = Boolean(usernameError || bioError)

  const hasOustandingAvatarChanges = emoji !== myProfile.avatar || color !== myProfile.color
  const hasOustandingProfileChanges =
    bio !== (myProfile.description || '') || username !== myProfile.displayName

  const handleChangeUserName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (usernameError) setUsernameError('')
    if (value.length <= 25) {
      setUsername(value)
    }
  }

  // TEMPORARY: AML Compliance - Handler kept for easy restoration of this field
  // const handleBioChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
  //   if (bioError) setBioError('')
  //   setBio(e.target.value)
  // }

  const handleSaveProfileChanges = () => {
    const { valid, errors } = validateInputs(username, bio)
    setUsernameError(errors.username || '')
    setBioError(errors.bio || '')
    if (valid && hasOustandingProfileChanges) saveProfile()
  }

  const handleSaveAvatarChanges = () => saveAvatar()

  const onDismissEditProfile = () => {
    setUsername(myProfile.displayName)
    setBio(myProfile.description ?? '')
    setEmoji(myProfile.avatar)
    setColor(myProfile.color)
    setUsernameError('')
    setBioError('')
    if (isEditingProfile) {
      setIsTrayOpen(false)
      onCompleteOnboarding && setTimeout(onCompleteOnboarding, 400)
    } else {
      setIsEditingProfile(true)
    }
  }

  const onEditAvatarClick = () => {
    setIsEditingProfile(false)
    trackBetLoopEditAvatar()
  }

  const onBackNav = () => {
    setIsEditingProfile(true)
    setEmoji(myProfile.avatar)
    setColor(myProfile.color)
  }

  const onEditProfileClick = () => {
    setIsTrayOpen(true)
    trackBetLoopEditMyProfile('My Profile')
  }

  return (
    <>
      {!shouldAutoOpenTray && (
        <Button color='secondary_outline' size='sm' isFullWidth onClick={onEditProfileClick}>
          Edit Profile
        </Button>
      )}

      {createPortal(
        <Tray
          isOpen={isTrayOpen || shouldAutoOpenTray === true}
          headerTitle={isEditingProfile ? 'Edit your profile' : 'Edit Avatar'}
          headerTitleAlignment='center'
          onDismiss={onDismissEditProfile}
          onBackNavigate={!isEditingProfile ? onBackNav : undefined}
          footerComponent={
            (!hasOustandingAvatarChanges && !isEditingProfile) ||
            (!hasOustandingProfileChanges && isEditingProfile) ? (
              <Button isFullWidth onClick={onDismissEditProfile}>
                Done
              </Button>
            ) : isEditingProfile ? (
              <Button
                isFullWidth
                disabled={isPendingProfile || hasError}
                onClick={handleSaveProfileChanges}
              >
                Save Changes
              </Button>
            ) : (
              <Button isFullWidth disabled={isPendingAvatar} onClick={handleSaveAvatarChanges}>
                Save Changes
              </Button>
            )
          }
        >
          {isEditingProfile ? (
            <EditProfileContainerStyled>
              <button type='button' onClick={onEditAvatarClick}>
                <ProfileAvatar avatar={myProfile.avatar} color={myProfile.color} size='lg' />
                <span>
                  <Icon name='LinePencil01' size='1.6rem' color='black' />
                </span>
              </button>

              <Input
                label='Username'
                name='Username'
                type='text'
                placeholder='Your username'
                value={username}
                helpText={
                  usernameError ? usernameError : 'Your username is visible to other TABtouch users'
                }
                isInvalid={!!usernameError}
                onChange={handleChangeUserName}
              />

              {/* TEMPORARY: AML Compliance - Bio/description edit field hidden */}
              {/* <TextArea
                name='Bio'
                label='Bio'
                value={bio}
                characterCount={350}
                helpText={bioError ? bioError : ''}
                isInvalid={!!bioError}
                placeholder='Write something about yourself'
                onChange={handleBioChange}
              /> */}
            </EditProfileContainerStyled>
          ) : (
            <EditAvatar
              profile={myProfile}
              setEmoji={setEmoji}
              setColor={setColor}
              emoji={emoji}
              color={color}
            />
          )}
        </Tray>,
        document.body
      )}
    </>
  )
}

// ================
// Local functions
// ================

const validateInputs = (username: string, bio: string) => {
  const errors: ValidationErrors = {}
  let valid = true

  if (username.trim().length < 2) {
    errors.username = 'Profile name must contain at least 2 characters'
    valid = false
  } else if (/[^a-zA-Z0-9\s.,'"-]/.test(username)) {
    errors.username = 'Profile name cannot contain special characters'
    valid = false
  }

  if (/^(?:[ \t]*[\r\n]){2,}/gm.test(bio)) {
    errors.bio = 'Bio cannot contain two consecutive empty lines'
    valid = false
  }

  return { valid, errors }
}

// ======
// Styles
// ======

const EditProfileContainerStyled = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.big,
  fontFamily: font.family.primary,
  fontSize: font.size.lg.fontSize,
  lineHeight: font.size.lg.lineHeight,
  letterSpacing: font.size.lg.letterSpacing,

  '> button:first-of-type': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    padding: 0,
    background: 0,
    border: 0,
    margin: 'auto',
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    transition: 'opacity 0.25s ease-out',

    ':hover': { opacity: 0.75 },
    ':active': { opacity: 0.5 },

    '> span': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      bottom: 0,
      right: 5,
      width: spacing.bigx3,
      height: spacing.bigx3,
      borderRadius: radius.full,
      border: `0.5px solid ${colors.neutral[200]}`,
      backgroundColor: colors.white,
      boxShadow: shadow.xs,
    },
  },
})

// ======
// Types
// ======

type ValidationErrors = {
  username?: string
  bio?: string
}
